import React, { useState } from 'react';
import {
    Table, Button, Drawer, Form, InputNumber, Space, Typography, Spin, Alert, Input,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
    SavingsProductDTO,
    useSavingsProducts,
    useUpdateSavingsProduct,
} from '../hooks/useSavingsProducts';

const { Title } = Typography;

export const SavingsProductListView: React.FC = () => {
    const { data: products = [], isLoading, error } = useSavingsProducts();
    const update = useUpdateSavingsProduct();

    const [editProduct, setEditProduct] = useState<SavingsProductDTO | null>(null);
    const [form] = Form.useForm<SavingsProductDTO>();

    const openEdit = (record: SavingsProductDTO) => {
        setEditProduct(record);
        form.setFieldsValue(record);
    };

    const handleSave = async () => {
        const values = await form.validateFields();
        if (!editProduct) return;
        update.mutate({ ...values, glNo: editProduct.glNo });
        setEditProduct(null);
    };

    const columns: ColumnsType<SavingsProductDTO> = [
        { title: 'GL No', dataIndex: 'glNo', key: 'glNo', width: 100 },
        { title: 'Savings GL', dataIndex: 'savGlno', key: 'savGlno', width: 100 },
        {
            title: 'Loan Guarantee %',
            dataIndex: 'lguarPer',
            key: 'lguarPer',
            width: 130,
            render: (v) => v != null ? `${v}%` : '—',
        },
        {
            title: 'Range 1 (0–X): Rate',
            key: 'range1',
            render: (_, r) =>
                r.range1fr != null
                    ? `${r.range1fr}–${r.range1to ?? '∞'} @ ${r.range1pe ?? 0}%`
                    : '—',
        },
        {
            title: 'Range 2: Rate',
            key: 'range2',
            render: (_, r) =>
                r.range2fr != null
                    ? `${r.range2fr}–${r.range2to ?? '∞'} @ ${r.range2pe ?? 0}%`
                    : '—',
        },
        {
            title: 'Range 3: Rate',
            key: 'range3',
            render: (_, r) =>
                r.range3fr != null
                    ? `${r.range3fr}–${r.range3to ?? '∞'} @ ${r.range3pe ?? 0}%`
                    : '—',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            render: (_, record) => (
                <Button
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() => openEdit(record)}
                >
                    Edit
                </Button>
            ),
        },
    ];

    if (error) {
        return <Alert type="error" message="Failed to load savings products" />;
    }

    return (
        <div style={{ padding: 24 }}>
            <Title level={4}>Savings Product Settings</Title>
            <Spin spinning={isLoading}>
                <Table
                    rowKey="glNo"
                    columns={columns}
                    dataSource={products}
                    pagination={false}
                    size="small"
                />
            </Spin>

            <Drawer
                title={`Edit: ${editProduct?.glNo}`}
                open={!!editProduct}
                onClose={() => setEditProduct(null)}
                width={480}
                footer={
                    <Space>
                        <Button onClick={() => setEditProduct(null)}>Cancel</Button>
                        <Button type="primary" loading={update.isPending} onClick={handleSave}>
                            Save
                        </Button>
                    </Space>
                }
            >
                <Form form={form} layout="vertical">
                    <Form.Item label="Savings GL Number" name="savGlno">
                        <Input maxLength={6} />
                    </Form.Item>
                    <Form.Item label="Loan Guarantee %" name="lguarPer">
                        <InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} addonAfter="%" />
                    </Form.Item>

                    <Title level={5}>Interest Range 1</Title>
                    <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
                        <Form.Item name="range1fr" label="From" style={{ flex: 1, marginRight: 4 }}>
                            <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                        <Form.Item name="range1to" label="To" style={{ flex: 1, marginRight: 4 }}>
                            <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                        <Form.Item name="range1pe" label="Rate %" style={{ flex: 1 }}>
                            <InputNumber style={{ width: '100%' }} min={0} step={0.01} addonAfter="%" />
                        </Form.Item>
                    </Space.Compact>

                    <Title level={5}>Interest Range 2</Title>
                    <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
                        <Form.Item name="range2fr" label="From" style={{ flex: 1, marginRight: 4 }}>
                            <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                        <Form.Item name="range2to" label="To" style={{ flex: 1, marginRight: 4 }}>
                            <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                        <Form.Item name="range2pe" label="Rate %" style={{ flex: 1 }}>
                            <InputNumber style={{ width: '100%' }} min={0} step={0.01} addonAfter="%" />
                        </Form.Item>
                    </Space.Compact>

                    <Title level={5}>Interest Range 3</Title>
                    <Space.Compact style={{ width: '100%' }}>
                        <Form.Item name="range3fr" label="From" style={{ flex: 1, marginRight: 4 }}>
                            <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                        <Form.Item name="range3to" label="To" style={{ flex: 1, marginRight: 4 }}>
                            <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                        <Form.Item name="range3pe" label="Rate %" style={{ flex: 1 }}>
                            <InputNumber style={{ width: '100%' }} min={0} step={0.01} addonAfter="%" />
                        </Form.Item>
                    </Space.Compact>
                </Form>
            </Drawer>
        </div>
    );
};
